import { useRef, useState } from "react";

// Lightweight, dependency-free drag & drop reordering for admin lists.
// Uses native HTML5 DnD via a dedicated drag HANDLE (so form inputs inside the
// card stay fully interactive). The whole card is the drop target.
// A ref mirrors the drag index so drag handlers stay synchronous (state alone
// would lag one commit behind, which matters for fast/synthetic events).
//
// Usage:
//   const { handleProps, itemProps, dragIndex, overIndex } = useDragReorder(list, onReorder);
//   <div {...itemProps(i)}><button {...handleProps(i)}><GripVertical/></button> ... </div>
export const useDragReorder = (items, onReorder) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const dragRef = useRef(null);

  const reset = () => { dragRef.current = null; setDragIndex(null); setOverIndex(null); };

  const handleProps = (index) => ({
    draggable: true,
    onDragStart: (e) => {
      dragRef.current = index;
      setDragIndex(index);
      e.dataTransfer.effectAllowed = "move";
      try { e.dataTransfer.setData("text/plain", String(index)); } catch { /* noop */ }
    },
    onDragEnd: reset,
  });

  const itemProps = (index) => ({
    onDragOver: (e) => {
      if (dragRef.current == null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (overIndex !== index) setOverIndex(index);
    },
    onDragLeave: () => { if (overIndex === index) setOverIndex(null); },
    onDrop: (e) => {
      e.preventDefault();
      const from = dragRef.current;
      const to = index;
      reset();
      if (from == null || from === to) return;
      const next = [...items];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      onReorder(next);
    },
  });

  return { handleProps, itemProps, dragIndex, overIndex };
};
